import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth'
import {
  Truck,
  Shield,
  MapPin,
  Clock,
  CreditCard,
  ChevronRight,
  CheckCircle,
  Star,
  ArrowRight,
  Phone,
  Mail,
  Navigation,
  Wrench,
  Battery,
  Fuel,
  Package
} from 'lucide-react'

export default function LandingPage() {
  const navigate = useNavigate()
  const { signOut } = useAuth()

  const handleSignIn = async () => {
    await signOut()
    navigate('/login')
  }

  const handleGetStarted = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        padding: '1rem 2rem',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--red)' }}>Mk</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)' }}>ononi</span>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={handleSignIn}
              style={{
                padding: '0.5rem 1.5rem',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600,
                color: 'var(--text)'
              }}
            >
              Sign In
            </button>
            <button
              onClick={handleGetStarted}
              style={{
                padding: '0.5rem 1.5rem',
                background: 'var(--red)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 600,
                color: 'white'
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
        padding: '4rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 800,
            color: 'var(--text)',
            marginBottom: '1rem',
            lineHeight: 1.2
          }}>
            One Breakdown. One Connected Journey.
          </h1>
          <p style={{
            fontSize: '1.25rem',
            color: 'var(--muted)',
            marginBottom: '2rem',
            lineHeight: 1.6
          }}>
            Kenya's premier roadside assistance platform. Get help when you need it, where you need it.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '1rem 2rem',
                background: 'var(--red)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 700,
                color: 'white',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Request Help Now
              <ArrowRight size={20} />
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: '1rem 2rem',
                background: 'white',
                border: '2px solid var(--red)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontWeight: 700,
                color: 'var(--red)',
                fontSize: '1rem'
              }}
            >
              Join as Partner
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section style={{ padding: '4rem 2rem', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--text)',
            textAlign: 'center',
            marginBottom: '1rem'
          }}>
            Our Services
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'var(--muted)',
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            Complete roadside assistance for every situation
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {[
              { icon: Truck, title: 'Towing Service', desc: '24/7 emergency towing to your preferred garage or destination', type: 'towing' },
              { icon: Battery, title: 'Battery Assistance', desc: 'Jump start or battery replacement services', type: 'battery' },
              { icon: Wrench, title: 'Flat Tyre Help', desc: 'Quick tyre change or repair wherever you are', type: 'flat-tyre' },
              { icon: Fuel, title: 'Fuel Delivery', desc: 'Emergency fuel delivery to get you back on the road', type: 'fuel' },
              { icon: Package, title: 'Spare Parts', desc: 'Order genuine parts from verified vendors', type: 'spare-parts' },
              { icon: Shield, title: 'Insurance Claims', desc: 'Hassle-free insurance claim processing', type: 'insurance' }
            ].map((service, index) => (
              <div 
                key={index} 
                onClick={() => navigate(`/service?type=${service.type}`)}
                style={{
                  padding: '2rem',
                  background: 'var(--bg)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--red)'
                  e.currentTarget.style.transform = 'translateY(-4px)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)'
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'var(--red-light)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <service.icon size={28} style={{ color: 'var(--red)' }} />
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'var(--text)',
                  marginBottom: '0.5rem'
                }}>
                  {service.title}
                </h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.5 }}>
                  {service.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ padding: '4rem 2rem', background: 'var(--bg)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--text)',
            textAlign: 'center',
            marginBottom: '1rem'
          }}>
            How It Works
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'var(--muted)',
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            Get help in 3 simple steps
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              { step: '1', title: 'Request Service', desc: 'Select your service type and location through our app or website' },
              { step: '2', title: 'Get Matched', desc: 'Our system connects you with nearby verified service providers' },
              { step: '3', title: 'Track & Complete', desc: 'Real-time tracking of your service until completion' }
            ].map((item, index) => (
              <div key={index} style={{ display: 'flex', gap: '1rem' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--red)',
                  color: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 800,
                  flexShrink: 0
                }}>
                  {item.step}
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: 'var(--text)',
                    marginBottom: '0.5rem'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.5 }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '4rem 2rem', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--text)',
            textAlign: 'center',
            marginBottom: '1rem'
          }}>
            Why Choose Mkononi?
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'var(--muted)',
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            The trusted name in roadside assistance across Kenya
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem'
          }}>
            {[
              { icon: MapPin, title: 'Live Tracking', desc: 'Track your service in real-time on the map' },
              { icon: Shield, title: 'Verified Providers', desc: 'All towers, garages, and parts vendors are verified' },
              { icon: Clock, title: '24/7 Availability', desc: 'Help available anytime, anywhere in Kenya' },
              { icon: CreditCard, title: 'M-Pesa Payments', desc: 'Easy and secure mobile money payments' },
              { icon: CheckCircle, title: 'Insurance Integration', desc: 'Direct insurance claim processing' },
              { icon: Star, title: 'Transparent Pricing', desc: 'Know exactly what you pay before service' }
            ].map((feature, index) => (
              <div key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--green-light)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <feature.icon size={20} style={{ color: 'var(--green)' }} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: 'var(--text)',
                    marginBottom: '0.25rem'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.5 }}>
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '4rem 2rem',
        background: 'var(--red)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 800,
            marginBottom: '1rem'
          }}>
            Ready to Get Started?
          </h2>
          <p style={{
            fontSize: '1.1rem',
            marginBottom: '2rem',
            opacity: 0.9
          }}>
            Join thousands of Kenyan motorists who trust Mkononi for their roadside assistance needs
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '1rem 2.5rem',
              background: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontWeight: 700,
              color: 'var(--red)',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Create Free Account
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: 'var(--text)',
        color: 'white',
        padding: '3rem 2rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--red)' }}>Mk</span>
                <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>ononi</span>
              </div>
              <p style={{ fontSize: '0.9rem', opacity: 0.8, lineHeight: 1.6 }}>
                Kenya's trusted roadside assistance platform. Connecting motorists with help, anytime, anywhere.
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>Services</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { name: 'Towing', type: 'towing' },
                  { name: 'Battery Assistance', type: 'battery' },
                  { name: 'Flat Tyre', type: 'flat-tyre' },
                  { name: 'Fuel Delivery', type: 'fuel' },
                  { name: 'Spare Parts', type: 'spare-parts' },
                  { name: 'Insurance Claims', type: 'insurance' }
                ].map((item) => (
                  <li 
                    key={item.type} 
                    onClick={() => navigate(`/service?type=${item.type}`)}
                    style={{ 
                      fontSize: '0.9rem', 
                      opacity: 0.8, 
                      marginBottom: '0.5rem', 
                      cursor: 'pointer',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>Partners</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { name: 'Tower Operators', type: 'tower-operators' },
                  { name: 'Garages', type: 'garages' },
                  { name: 'Parts Vendors', type: 'parts-vendors' },
                  { name: 'Insurance Providers', type: 'insurance-providers' }
                ].map((item) => (
                  <li 
                    key={item.type} 
                    onClick={() => navigate(`/service?type=${item.type}`)}
                    style={{ 
                      fontSize: '0.9rem', 
                      opacity: 0.8, 
                      marginBottom: '0.5rem', 
                      cursor: 'pointer',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>Contact</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <a 
                  href="tel:+254700000000"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    fontSize: '0.9rem', 
                    opacity: 0.8,
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                >
                  <Phone size={16} />
                  <span>+254 700 000 000</span>
                </a>
                <a 
                  href="mailto:info@mkononi.co.ke"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    fontSize: '0.9rem', 
                    opacity: 0.8,
                    color: 'white',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                >
                  <Mail size={16} />
                  <span>info@mkononi.co.ke</span>
                </a>
              </div>
            </div>
          </div>
          <div style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            paddingTop: '2rem',
            textAlign: 'center',
            fontSize: '0.85rem',
            opacity: 0.6
          }}>
            © 2026 Mkononi. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
